// =====================================================================
// Faz 14a (2026-04-30) — Welcome Email Otomasyonu raporu
// =====================================================================
// 4-adimlik drip-series funnel:
//   1) register     — Email dogrulandi (auth.js POST /verify trigger)
//   2) approved     — Admin onayi (users.js PUT /:id/approve trigger)
//   3) reminder_7d  — Onay+7g siparis yok (welcome-worker cron, saatte 1 tick)
//   4) first_order  — Ilk siparis (orders.js POST / trigger)
//
// KPI'lar: Funnel sayilari (her adimi gecen unique user) + register->first_order
// donusum yuzdesi. Son 10 mail kaydi tablo.
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Button, IconButton, Tooltip, LinearProgress
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import VerifiedIcon from "@mui/icons-material/Verified";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtDateTime = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "—";

const STEP_META = {
  register:    { label: "1. Kayıt",         color: "#1565C0", icon: <HowToRegIcon /> },
  approved:    { label: "2. Onaylandı",     color: "#10B981", icon: <VerifiedIcon /> },
  reminder_7d: { label: "3. 7g Hatırlatma", color: "#F59E0B", icon: <ScheduleSendIcon /> },
  first_order: { label: "4. İlk Sipariş",   color: "#8B5CF6", icon: <ShoppingCartCheckoutIcon /> }
};

const STATUS_CHIPS = {
  sent:    <Chip size="small" label="✅ Gönderildi" color="success" />,
  failed:  <Chip size="small" label="❌ Başarısız" color="error" />,
  skipped: <Chip size="small" label="⏭ Atlandı" variant="outlined" />
};

const FunnelStep = ({ step, count, prevCount, color, icon }) => {
  const pct = prevCount > 0 ? Math.round((count / prevCount) * 100) : (count > 0 ? 100 : 0);
  return (
    <Card sx={{ p: 2.2, borderRadius: 3, height: "100%" }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
        <Box sx={{ bgcolor: color, color: "white", borderRadius: 2, p: 1, display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>{STEP_META[step].label}</Typography>
      </Box>
      <Typography variant="h4" fontWeight={800} color="text.primary">
        {count.toLocaleString("tr-TR")}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {prevCount > 0
          ? `Önceki adımdan: %${pct}`
          : "—"}
      </Typography>
      {prevCount > 0 && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, pct)}
          sx={{ mt: 1.5, height: 6, borderRadius: 3, bgcolor: "#f0f0f0", '& .MuiLinearProgress-bar': { bgcolor: color } }}
        />
      )}
    </Card>
  );
};

export default function WelcomeEmail() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/welcome/stats")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  const runReminder = async () => {
    if (!window.confirm("7 gün önce onaylanmış ama henüz sipariş vermemiş kullanıcılara şimdi reminder_7d maili gönder?")) return;
    setRunning(true);
    try {
      const r = await API.post("/api/admin/welcome/run-reminder");
      const res = r.data.result || {};
      toast.success(`Reminder tick — gönderilen: ${res.sent || 0}, atlanan: ${res.failed || 0}`);
      load();
    } catch (e) {
      toast.error("Hata: " + (e.response?.data?.error || e.message));
    } finally {
      setRunning(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>;
  }
  if (err && !data) {
    return <Alert severity="error" sx={{ mt: 4 }}>Veri yüklenemedi: {err}</Alert>;
  }

  const f = data?.funnel || {};
  const recent = data?.recent || [];

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={800}>👋 Hoş Geldin Email Serisi</Typography>
          <Typography variant="body2" color="text.secondary">
            4 adımlık drip-series — kayıt → onay → 7g hatırlatma → ilk sipariş (son 90 gün)
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="reminder_7d cron'u şimdi tetikle">
            <span>
              <Button
                variant="outlined"
                startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={runReminder}
                disabled={running}
                size="small"
              >
                Reminder Tick
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Yenile"><IconButton onClick={load} size="small"><RefreshIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Funnel — 4 adım */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <FunnelStep step="register" count={f.step_register || 0} prevCount={0} color={STEP_META.register.color} icon={STEP_META.register.icon} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FunnelStep step="approved" count={f.step_approved || 0} prevCount={f.step_register || 0} color={STEP_META.approved.color} icon={STEP_META.approved.icon} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FunnelStep step="reminder_7d" count={f.step_reminder_7d || 0} prevCount={f.step_approved || 0} color={STEP_META.reminder_7d.color} icon={STEP_META.reminder_7d.icon} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FunnelStep step="first_order" count={f.step_first_order || 0} prevCount={f.step_approved || 0} color={STEP_META.first_order.color} icon={STEP_META.first_order.icon} />
        </Grid>
      </Grid>

      {/* Conversion summary */}
      <Card sx={{ p: 2.5, borderRadius: 3, mb: 3, bgcolor: '#F0FDF4', border: '1px solid #86EFAC' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
              KAYIT → İLK SİPARİŞ DÖNÜŞÜMÜ
            </Typography>
            <Typography variant="h3" fontWeight={800} color="#16A34A">
              %{f.register_to_order_pct || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {f.step_register || 0} kayıttan {f.step_first_order || 0} müşteri ilk siparişi verdi
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">B2B endüstri ortalaması</Typography>
            <Typography variant="h6" color="text.disabled">%15-25</Typography>
          </Box>
        </Box>
      </Card>

      {/* Son mail kayıtları */}
      <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
          <Typography variant="subtitle1" fontWeight={700}>Son 10 Welcome Mail</Typography>
        </Box>
        {recent.length === 0 ? (
          <Box p={6} textAlign="center" color="text.secondary">
            <Typography>Henüz gönderilmiş welcome maili yok.</Typography>
            <Typography variant="caption">Yeni kullanıcı email doğrulayınca, admin onayında veya ilk sipariş verince otomatik tetiklenir.</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8f9fa" }}>
                <TableRow>
                  <TableCell width={150}>Zaman</TableCell>
                  <TableCell width={140}>Adım</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell width={140} align="center">Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontSize: 12 }}>{fmtDateTime(r.sent_at)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STEP_META[r.step]?.label || r.step}
                        sx={{ bgcolor: STEP_META[r.step]?.color, color: "white", fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.name || r.company || "—"}</Typography>
                      <Typography variant="caption" color="text.disabled">{r.email}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {STATUS_CHIPS[r.status] || <Chip size="small" label={r.status} />}
                      {r.error_message && (
                        <Tooltip title={r.error_message}>
                          <Box sx={{ fontSize: 10, color: '#c00', mt: 0.5 }}>⚠ Hata</Box>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </Box>
  );
}
